import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Building2, 
  Users, 
  Search,
  Eye,
  Shield,
  UserPlus,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("companies");
  
  // Companies data
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState("");
  
  // Users data
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    checkAccess();
    if (activeTab === "companies") {
      fetchCompanies();
    } else if (activeTab === "users") {
      fetchAllUsers();
    }
  }, [activeTab]);

  const checkAccess = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
      if (!response.data.is_admin) {
        toast.error("Access denied");
        navigate('/');
      }
    } catch (error) {
      navigate('/');
    }
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const usersResp = await axios.get(`${BACKEND_URL}/api/auth/users`);
      const users = usersResp.data || [];
      
      const subsResp = await axios.get(`${BACKEND_URL}/api/enterprise/subsidiaries`);
      const subsidiaries = subsResp.data || [];
      
      // Group by company
      const companyMap = new Map();
      users.forEach(u => {
        if (!companyMap.has(u.id)) {
          companyMap.set(u.id, {
            id: u.id,
            name: u.company_name || u.email,
            owner: u.email,
            subsidiaries: subsidiaries.filter(s => s.company_id === u.id).length,
            users: 1,
            status: u.is_active ? 'Active' : 'Inactive',
            created: u.created_at
          });
        } else {
          companyMap.get(u.id).users++;
        }
      });
      
      setCompanies(Array.from(companyMap.values()));
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/users`);
      setAllUsers(response.data || []);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(companySearch.toLowerCase()) ||
    c.owner.toLowerCase().includes(companySearch.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                         (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase());
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && u.is_active) ||
                         (statusFilter === "inactive" && !u.is_active);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold">Platform Administration</h1>
        </div>
        <p className="text-gray-600">Manage all companies and users</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="companies">
            <Building2 className="h-4 w-4 mr-2" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="h-4 w-4 mr-2" />
            All Users
          </TabsTrigger>
        </TabsList>

        {/* COMPANIES TAB */}
        <TabsContent value="companies">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>All Companies ({companies.length})</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search companies..."
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Subsidiaries</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Users</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredCompanies.map(company => (
                      <tr key={company.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{company.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{company.owner}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">{company.subsidiaries}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">{company.users}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={company.status === 'Active' ? 'default' : 'secondary'}>
                            {company.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>All Users ({allUsers.length})</CardTitle>
                <div className="flex gap-3">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Admin</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers.map(user => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{user.full_name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.company_name || user.email}</td>
                        <td className="px-4 py-3">
                          <Badge variant={user.is_active ? 'default' : 'secondary'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {user.is_admin && <Shield className="h-4 w-4 text-blue-600 inline" />}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PlatformAdminDashboard;
